import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Dashboard/Sidebar';

interface JobRole {
  companyName: string;
  jobTitle: string;
  jobType: string;
  location: string;
  deadline: string;
  applyLink: string;
}

export const GraduateRolesPage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobRole[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const jobsPerPage = 12;

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Load and parse CSV data
  useEffect(() => {
    const loadJobsFromCSV = async () => {
      try {
        setLoadingJobs(true);
        const response = await fetch('/static-job-list.csv');
        const csvText = await response.text();
        
        // Parse CSV
        const lines = csvText.split('\n');
        const parsedJobs: JobRole[] = [];
        
        // Skip header row (index 0) and parse data rows
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Handle CSV parsing with commas inside quotes
          const values = parseCSVLine(line);
          
          if (values.length >= 6) {
            parsedJobs.push({
              companyName: values[0],
              jobTitle: values[1],
              jobType: values[2],
              location: values[3],
              deadline: values[4],
              applyLink: values[5],
            });
          }
        }
        
        setJobs(parsedJobs);
      } catch (error) {
        console.error('Error loading jobs:', error);
      } finally {
        setLoadingJobs(false);
      }
    };

    loadJobsFromCSV();
  }, []);

  // Parse CSV line handling quoted fields with commas
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    if (current) {
      result.push(current.trim());
    }
    
    return result;
  };

  // Pagination calculations
  const totalJobs = jobs.length;
  const totalPages = Math.ceil(totalJobs / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = Math.min(startIndex + jobsPerPage, totalJobs);
  const currentJobs = jobs.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <div className="main-content">
        <div className="max-w-7xl mx-auto h-full flex flex-col">
          {/* Header Section */}
          <div className="mb-4 md:mb-6">
            <h1 className="heading-1 mb-2">
              <span className="text-gradient-blue">Graduate Roles</span> List
            </h1>
            <p className="text-body text-sm md:text-lg">
              Explore graduate opportunities across the UK
            </p>
          </div>

          {/* Jobs Count Banner */}
          <div className="mb-4">
            <div className="glass-panel rounded-xl p-3 md:p-4 border-l-4 border-blue-500 flex items-center gap-3 md:gap-4">
              <div className="p-1.5 md:p-2 bg-blue-50 rounded-lg">
                <svg className="h-4 w-4 md:h-5 md:w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs md:text-sm font-medium text-slate-900">
                  {loadingJobs ? 'Loading...' : `Showing ${startIndex + 1}-${endIndex} of ${totalJobs} jobs`}
                </p>
              </div>
            </div>
          </div>

          {/* Table/Cards Section */}
          <div className="flex-1 flex flex-col min-h-0">
            {loadingJobs ? (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8 flex-1 flex items-center justify-center">
                <p className="text-slate-500 font-medium text-sm md:text-base">Loading opportunities...</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="lg:hidden space-y-3 flex-1 overflow-y-auto pb-4">
                  {currentJobs.map((job, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 animate-fade-in"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 text-sm truncate">{job.companyName}</h3>
                          <p className="text-slate-700 font-medium text-xs mt-0.5 line-clamp-2">{job.jobTitle}</p>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 whitespace-nowrap flex-shrink-0">
                          {job.jobType}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {job.deadline || 'Rolling'}
                        </span>
                      </div>
                      <a
                        href={job.applyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                      >
                        Apply Now
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:flex bg-white rounded-2xl border border-slate-100 shadow-sm flex-1 flex-col overflow-hidden animate-fade-in">
                  <div className="flex-1 overflow-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Company
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Job Title
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                            Type
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Location
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Deadline
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {currentJobs.map((job, index) => (
                          <tr 
                            key={index}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <td className="px-6 py-4">
                              <span className="font-semibold text-slate-900">
                                {job.companyName}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-slate-700 font-medium">
                                {job.jobTitle}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                                {job.jobType}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-slate-600 text-sm">
                                {job.location}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm font-medium whitespace-nowrap ${
                                job.deadline ? 'text-slate-700' : 'text-slate-400'
                              }`}>
                                {job.deadline || 'Rolling'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <a
                                href={job.applyLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm hover:shadow-md whitespace-nowrap"
                              >
                                Apply
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                  <div className="border-t border-slate-200 px-3 md:px-6 py-3 md:py-4 bg-white lg:bg-slate-50 rounded-b-xl lg:rounded-none mt-3 lg:mt-0 flex items-center justify-between gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 text-xs md:text-sm font-semibold rounded-lg transition-colors ${
                        currentPage === 1
                          ? 'text-slate-400 bg-slate-100 cursor-not-allowed'
                          : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-sm'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span className="hidden sm:inline">Previous</span>
                    </button>

                    <div className="flex items-center gap-1 md:gap-2">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 md:px-3 py-2 text-slate-400 text-xs md:text-sm">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageClick(page as number)}
                            className={`px-2.5 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 text-xs md:text-sm font-semibold rounded-lg transition-colors ${
                        currentPage === totalPages
                          ? 'text-slate-400 bg-slate-100 cursor-not-allowed'
                          : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-sm'
                      }`}
                    >
                      <span className="hidden sm:inline">Next</span>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
